'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Tampons & Period Products: 2024 Berkeley Study"
      author="MicroPlastics Editorial Team"
      date="July 20, 2025"
      readTime="11 min read"
      heroImage="/blog/app-search.png"
      heroImageAlt="Microplastics in tampons and period products. Berkeley 2024 study"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        UC Berkeley&apos;s landmark July 2024 study (<em>Environment International</em>)
        tested 30 tampons across 14 brands and 18 product lines, and found
        <strong> 16 different metals including lead, arsenic, and cadmium in 100%
        of samples</strong>. Period products are typically polypropylene (string,
        applicator, liner), polyethylene (backing), and viscose-rayon (absorbent
        core), direct microplastic and chemical exposure to one of the body&apos;s
        most absorbent tissues. The safest alternatives are <strong>100% organic
        cotton tampons (Saalt, Cora, Honest), medical-grade silicone menstrual
        cups (Saalt, Diva, Lunette), and period underwear (Thinx, Knix)</strong>.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>The Shearston et al. 2024 UC Berkeley study found <strong>16 metals in 100% of 30 tampon samples</strong> across 14 brands.</>,
          <>Vaginal tissue has higher absorption potential than skin elsewhere, chemical migration is biologically significant.</>,
          <>Conventional tampons typically contain polypropylene (applicator, string), polyethylene (backing), and viscose-rayon (core).</>,
          <>Period pads typically contain 90%+ plastic by weight, polyethylene backing, polypropylene topsheet, superabsorbent polymer (SAP) core.</>,
          <>Medical-grade silicone menstrual cups (Saalt, Diva), 100% organic cotton tampons (Saalt, Cora), and period underwear (Thinx, Knix) are the safest alternatives.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The 2024 Berkeley study that changed the conversation
      </h2>
      <p>
        Jenni Shearston and colleagues at the UC Berkeley School of Public
        Health published their tampon analysis in <em>Environment International</em>
        in July 2024. Testing 30 tampons from 14 brands and 18 product lines
        purchased in the US and Europe, they screened for 16 metals using
        ICP-MS analysis.
      </p>
      <p>The findings were striking:</p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>100% of samples contained measurable metals</strong> across all 16 elements tested.</li>
        <li>Lead, arsenic, cadmium, and mercury were among those detected.</li>
        <li>Organic tampons had lower arsenic on average but <em>higher</em> lead than non-organic, suggesting different contamination pathways.</li>
        <li>Both US and European products tested similarly, this is a global supply chain issue.</li>
        <li>The researchers chose not to name brands to avoid implying that uncontaminated alternatives exist (none in their sample did).</li>
      </ul>
      <p>
        The study was deliberately designed not to estimate health effects, it
        was a first-detection paper. Whether metals migrate from the tampon
        into vaginal tissue and at what dose has not been quantified yet.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Beyond metals: the microplastic component
      </h2>
      <p>
        Tampons and pads have multiple plastic components:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Applicators</strong>, polypropylene or cardboard.</li>
        <li><strong>String</strong>, polypropylene (cotton-string organic alternatives exist).</li>
        <li><strong>Tampon core</strong>, typically a viscose-rayon + cotton blend; viscose is a semi-synthetic fibre.</li>
        <li><strong>Pad backing</strong>, polyethylene film (the &ldquo;leakproof&rdquo; bottom layer).</li>
        <li><strong>Pad topsheet</strong>, polypropylene nonwoven.</li>
        <li><strong>Pad absorbent core</strong>, superabsorbent polymer (SAP), typically sodium polyacrylate.</li>
      </ul>
      <p>
        A 2022 RSC <em>Environmental Science: Nano</em> study estimated that
        period products release significant microplastic fibres during use
        and disposal, both into the body and the environment.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Period product options compared
      </h2>

      <BlogComparisonTable
        caption="Period products by microplastic and chemical safety"
        columns={[
          { key: 'product', label: 'Product' },
          { key: 'safety', label: 'Microplastic / chemical safety' },
          { key: 'notes', label: 'Notes' },
        ]}
        rows={[
          { product: 'Medical-grade silicone menstrual cup (Saalt, Diva, Lunette, OrganiCup)', safety: 'Very low', notes: '10-year lifespan; reusable; medical-grade silicone' },
          { product: 'Menstrual disc (Saalt Disc, Flex)', safety: 'Very low (silicone) or low (TPE)', notes: 'Similar to cups; different fit' },
          { product: 'Period underwear (Thinx, Knix, Saalt)', safety: 'Low-moderate (check for PFAS - Thinx had a 2022 PFAS recall)', notes: 'Reusable; check current PFAS-free certification' },
          { product: '100% organic cotton tampon (Saalt, Cora, Honest, Lola, Rael)', safety: 'Low (no synthetic core, plastic-free string)', notes: 'Choose &ldquo;applicator-free&rdquo; or cardboard-applicator versions' },
          { product: 'Organic cotton pads (Natracare, Rael, Cora)', safety: 'Low-moderate (cotton core, may still have some plastic backing)', notes: 'Check for &ldquo;fully biodegradable&rdquo; certification' },
          { product: 'Reusable cloth pads (GladRags, Hannahpad)', safety: 'Very low (cotton)', notes: 'Reusable; cloth backing only' },
          { product: 'Conventional tampon (most major brands)', safety: 'Moderate (metals + plastics)', notes: 'Subject of 2024 Berkeley findings' },
          { product: 'Conventional pad (most major brands)', safety: 'Higher (90%+ plastic by weight + SAP)', notes: 'Heaviest plastic exposure category' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The 4 highest-impact switches
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Switch to a medical-grade silicone menstrual cup.</strong> Single biggest reduction. Saalt, Diva Cup, Lunette, OrganiCup all carry medical-grade silicone certification. 10-year lifespan.</li>
        <li><strong>If using tampons, choose 100% organic cotton with cardboard or no applicator.</strong> Saalt, Cora, Honest, Lola, Rael.</li>
        <li><strong>If using pads, choose 100% organic cotton or reusable cloth pads.</strong> Natracare for disposable; GladRags or Hannahpad for reusable.</li>
        <li><strong>Period underwear is convenient but verify PFAS-free.</strong> Thinx had a 2022 settlement over PFAS contamination; check current third-party certifications before buying.</li>
      </ol>

      <p>
        See related:{' '}
        <Link href="/blog/microplastics-and-fertility" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics and fertility
        </Link>,{' '}
        <Link href="/blog/microplastics-in-cosmetics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in cosmetics
        </Link>, and{' '}
        <Link href="/blog/microplastics-and-skin-absorption" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics and skin absorption
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Ingredient list parsed from the product label or barcode.</>,
          <>Flagged ingredients, polyethylene, acrylates, carbomer, PEG, fragrance, parabens.</>,
          <>Product category, leave-on vs rinse-off; risk weighted differently.</>,
          <>Brand and product line, clean certifications (EWG VERIFIED, MADE SAFE).</>,
          <>Cited research and regulatory references for each scan.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan personal-care products before buying"
        sub="The MicroPlastics app reads the ingredient list, flags microplastic polymers and additives, and points to cleaner alternatives in the same category."
        buttonLabel="Scan cosmetics in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Are tampons safe?',
            answer:
              'The 2024 UC Berkeley study found 16 metals including lead and arsenic in 100% of 30 tampons tested across 14 brands. The study did not estimate health risk but raised significant public health concerns given that vaginal tissue has high absorption potential. Safer alternatives include medical-grade silicone menstrual cups and 100% organic cotton tampons.',
          },
          {
            question: 'Do tampons contain microplastics?',
            answer:
              'Yes. Conventional tampons typically contain polypropylene (applicator, string), polyethylene (backing), and viscose-rayon (a semi-synthetic core fibre). Pads contain 90%+ plastic by weight including polyethylene backing, polypropylene topsheet, and superabsorbent polymer.',
          },
          {
            question: 'Are menstrual cups safer than tampons?',
            answer:
              'Yes, by every available measure. Medical-grade silicone menstrual cups (Saalt, Diva, Lunette, OrganiCup) eliminate exposure to both microplastics and metals found in tampons. They are reusable for up to 10 years, making them dramatically cheaper over time.',
          },
          {
            question: 'Are organic cotton tampons better?',
            answer:
              'Yes for plastics. 100% organic cotton tampons (Saalt, Cora, Honest, Lola, Rael) eliminate synthetic core fibres. The 2024 Berkeley study found organic tampons had lower arsenic but higher lead than non-organic, so the metals story is more complex. Still meaningfully safer overall.',
          },
          {
            question: 'Is period underwear safe?',
            answer:
              'It depends on the brand. Thinx settled a class-action lawsuit in 2022 over PFAS chemical contamination. Knix, Saalt, and others claim PFAS-free; verify current third-party certifications before buying. Period underwear is generally lower-exposure than conventional disposable products if PFAS-free.',
          },
          {
            question: 'What is the safest period product overall?',
            answer:
              'Medical-grade silicone menstrual cups (Saalt, Diva, Lunette) eliminate both microplastic and metal exposure, last up to 10 years, and have the lowest lifetime cost. They are the most evidence-supported alternative to conventional tampons and pads.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Shearston JA, Upson K, Gordon M, et al.',
            year: 2024,
            title: 'Tampons as a source of exposure to metal(loid)s',
            publisher: 'Environment International',
            url: 'https://www.sciencedirect.com/science/article/pii/S0160412024004161',
          },
          {
            authors: 'Bouwmeester H, Schroeder D, Mauricio R, et al.',
            year: 2022,
            title: 'Release of microplastic fibres and fragmentation to billions of nanoplastics from period products',
            publisher: 'Environmental Science: Nano (RSC)',
            url: 'https://pubs.rsc.org/en/content/articlelanding/2022/en/d1en00755f',
          },
          {
            authors: 'Davis EL, Sandhu KS, Lyons-Eubanks H, et al.',
            year: 2025,
            title: 'Plastic Additives in Single-Use and Reusable Menstrual Products',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.5c09064',
          },
          {
            authors: 'US Food and Drug Administration',
            year: 2024,
            title: 'Tampons: Frequently Asked Questions',
            publisher: 'FDA',
            url: 'https://www.fda.gov/medical-devices/menstrual-tampons-and-pads-information-premarket-notification-submissions-510ks-guidance-industry',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
